import * as React from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Skeleton,
  SkeletonItem,
} from '@fluentui/react-components';

/**
 * Loading-state skeleton for the customer profile (CustomerProfileComponent).
 * Replaces the centered "Loading customer data…" spinner so the page paints
 * its real shape — breadcrumb header, tab row, and the two-column information
 * form — while the /booqable/customers/:id fetch is in flight.
 */

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    ...shorthands.padding('16px', '24px'),
    width: '100%',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap('12px'),
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  tabs: {
    display: 'flex',
    ...shorthands.gap('16px'),
    ...shorthands.padding('8px', '0'),
  },
  columns: {
    display: 'flex',
    ...shorthands.gap('16px'),
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    flexGrow: 1,
    flexBasis: '0',
    minWidth: '280px',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
    ...shorthands.padding('16px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
  },
  w80: { width: '80px' },
  w120: { width: '120px' },
  w160: { width: '160px' },
  w200: { width: '200px' },
  grow: { flexGrow: 1 },
});

const SectionSkeleton: React.FC<{ rows?: number }> = ({ rows = 3 }) => {
  const styles = useStyles();
  return (
    <Skeleton aria-label="Loading" className={styles.card}>
      <div className={styles.w160}>
        <SkeletonItem size={16} />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className={styles.row}>
          <div className={styles.w120}>
            <SkeletonItem size={12} />
          </div>
          <div className={styles.grow}>
            <SkeletonItem size={12} />
          </div>
        </div>
      ))}
    </Skeleton>
  );
};

export const CustomerProfileSkeleton: React.FC = () => {
  const styles = useStyles();
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.breadcrumb}>
          <div className={styles.w120}>
            <SkeletonItem size={20} />
          </div>
          <SkeletonItem shape="circle" size={24} />
          <div className={styles.w200}>
            <SkeletonItem size={20} />
          </div>
        </div>
        <Skeleton aria-label="Loading">
          <div className={styles.w120}>
            <SkeletonItem size={32} />
          </div>
        </Skeleton>
      </div>
      <div className={styles.tabs}>
        <div className={styles.w80}>
          <SkeletonItem size={16} />
        </div>
        <div className={styles.w80}>
          <SkeletonItem size={16} />
        </div>
        <div className={styles.w80}>
          <SkeletonItem size={16} />
        </div>
      </div>
      <div className={styles.columns}>
        <div className={styles.column}>
          <SectionSkeleton rows={4} />
          <SectionSkeleton rows={3} />
        </div>
        <div className={styles.column}>
          <SectionSkeleton rows={4} />
          <SectionSkeleton rows={2} />
        </div>
      </div>
    </div>
  );
};

export default CustomerProfileSkeleton;
